/**
 * Threat messages blocked vs delivered over the trend window.
 *
 * The absolute volume of threat mail measures the internet, not Norican — so
 * the signal here is the *ratio*. Blocked means the control worked; delivered
 * means a threat reached a mailbox and the control failed. Both trends are drawn
 * so a rise in delivered mail is distinguishable from a rise in everything, and
 * the day strip marks which specific days let something through.
 */

import { MiniSparkline, SegmentBar, StatusStrip, Typography } from "@norican/react";

import type { Verdict } from "@/components/board";
import type { SecuritySummary, SecurityTrendPoint } from "@/lib/api";

import { PanelCard } from "./panel-card";
import {
  DIRECTION_COLOR,
  formatCount,
  formatDayInitial,
  formatDayLabel,
  formatPct,
} from "./status";

/** Days shown in the per-day strip. The sparklines carry the full window. */
const STRIP_DAYS = 14;

interface MessageTrendProps {
  /** Per-day blocked/delivered counts, oldest first. */
  points: SecurityTrendPoint[];
  /** Headline figures, for the composition totals and the thresholds. */
  summary: SecuritySummary;
}

export function MessageTrend({ points, summary }: MessageTrendProps) {
  const total = summary.blocked + summary.delivered;
  const strip = points.slice(-STRIP_DAYS);

  return (
    <PanelCard
      title="Threat messages — blocked vs delivered"
      caption={`Last ${summary.window_days} days · ${formatCount(total)} threat observations`}
      verdict={messageTrendVerdict(summary)}
      note={
        <Typography variant="caption" tone="muted">
          Counts are threat observations, not people: one message carrying two threats counts twice,
          and no recipient, sender or subject is stored. A delivered threat is the control failing —
          it warns at {formatPct(summary.delivered_share_warn_pct, 0)} of the window and escalates
          at {formatPct(summary.delivered_share_danger_pct, 0)}.
        </Typography>
      }
    >
      {total === 0 ? (
        <Typography variant="body-sm" tone="muted">
          No threat mail has been recorded in this window. That is a genuine zero from the rollups,
          not a placeholder — check integration health below if it looks wrong.
        </Typography>
      ) : (
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-2">
            <Typography variant="caption" tone="muted">
              Window composition
            </Typography>
            <SegmentBar
              segments={[
                {
                  value: summary.blocked,
                  label: `Blocked (${formatCount(summary.blocked)})`,
                  color: DIRECTION_COLOR.blocked,
                },
                {
                  value: summary.delivered,
                  label: `Delivered (${formatCount(summary.delivered)})`,
                  color: DIRECTION_COLOR.delivered,
                },
              ]}
              size="lg"
              showLegend
              aria-label="Threat messages blocked versus delivered over the window"
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <TrendCell
              label="Blocked per day"
              value={formatCount(summary.blocked)}
              series={points.map((point) => point.blocked)}
              tone="success"
            />
            <TrendCell
              label="Delivered per day"
              value={formatCount(summary.delivered)}
              series={points.map((point) => point.delivered)}
              tone={summary.delivered > 0 ? "danger" : "neutral"}
            />
          </div>

          {strip.length > 0 && (
            <div className="flex flex-col gap-2">
              <Typography variant="caption" tone="muted">
                Last {strip.length} days — a red cell is a day something got through
              </Typography>
              <StatusStrip aria-label="Days on which a threat message was delivered">
                {strip.map((point) => (
                  <StatusStrip.Cell
                    key={point.day}
                    label={formatDayInitial(point.day)}
                    status={cellStatus(point)}
                    title={`${formatDayLabel(point.day)} — ${formatCount(point.blocked)} blocked, ${formatCount(
                      point.delivered,
                    )} delivered (${formatPct(point.delivered_share_pct)} delivered)`}
                  >
                    {point.delivered > 0 ? formatCount(point.delivered) : "·"}
                  </StatusStrip.Cell>
                ))}
              </StatusStrip>
            </div>
          )}
        </div>
      )}
    </PanelCard>
  );
}

interface TrendCellProps {
  /** What the series measures. */
  label: string;
  /** The window total, already formatted. */
  value: string;
  /** Per-day values, oldest first. */
  series: number[];
  /** Sparkline tone. */
  tone: "success" | "danger" | "neutral";
}

function TrendCell({ label, value, series, tone }: TrendCellProps) {
  return (
    <div className="border-border flex items-center justify-between gap-4 rounded-md border p-3">
      <div className="flex flex-col">
        <Typography variant="caption" tone="muted">
          {label}
        </Typography>
        <Typography variant="heading-5" as="p" tabular>
          {value}
        </Typography>
      </div>
      {series.length > 1 && (
        <MiniSparkline
          data={series}
          tone={tone}
          endMarker="dot"
          width={140}
          height={32}
          aria-label={`${label} trend`}
        />
      )}
    </div>
  );
}

/** RAG tone for one day in the strip. `StatusStrip` uses the finance vocabulary. */
function cellStatus(point: SecurityTrendPoint): "positive" | "caution" | "negative" | "neutral" {
  if (point.blocked === 0 && point.delivered === 0) {
    // No data and no threats look the same in a rollup, so neither is claimed
    // as a good day.
    return "neutral";
  }
  if (point.delivered === 0) {
    return "positive";
  }
  return point.delivered > 1 ? "negative" : "caution";
}

/** The ratio is the signal, so the verdict speaks to the ratio. */
function messageTrendVerdict(summary: SecuritySummary): Verdict {
  const total = summary.blocked + summary.delivered;
  if (total === 0) {
    return {
      label: "No data",
      tone: "neutral",
      text: "No threat mail recorded in this window, so there is no ratio to read.",
    };
  }
  if (summary.delivered === 0) {
    return {
      label: "Holding",
      tone: "success",
      text: "Nothing got through in this window. The absolute volume measures the internet; this line measures us.",
    };
  }
  const share = (summary.delivered / total) * 100;
  const tone: Verdict["tone"] =
    share >= summary.delivered_share_danger_pct
      ? "danger"
      : share >= summary.delivered_share_warn_pct
        ? "warning"
        : "success";
  return {
    label: tone === "success" ? "Holding" : tone === "warning" ? "Watch" : "Act",
    tone,
    text: `${formatPct(share)} of threat observations were delivered rather than blocked, against a ${formatPct(summary.delivered_share_warn_pct, 0)} warning line. Every delivered threat is the control failing, not degrading.`,
  };
}
